import { useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Avatar, Box, Button, FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Select,
  Stack, SvgIcon, TextField, Typography, Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HighlightOffIcon from '@mui/icons-material/HighlightOffOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SearchIcon from '@mui/icons-material/Search';
import StoreOutlinedIcon from '@mui/icons-material/StoreOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { DetailPageTemplate } from '../../../../components/page-templates/detail-page/DetailPageTemplate';
import { TabDefinition } from '../../../../components/shared/tabs/TabbedSection';
import { ConfirmationDialog } from '../../../../components/shared/dialogs/ConfirmationDialog';
import { AuditHistoryPanel } from '../../../../components/shared/audit/AuditHistoryPanel';
import { ChangeLogTarget } from '../../../../enums/ChangeLogTarget';
import MessagesSnackbar from '../../../../components/shared/MessagesSnackbar';
import { InsightTile } from '../../../../components/shared/tiles/InsightTile';
import { PictureTile } from '../../../../components/shared/tiles/PictureTile';
import { UserContext } from '../../../../components/shared/useUser';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import useLogError from '../../../../hooks/useLogError';
import { isEmailValid, formatUtcToLocalDateTime } from '../../../../utils';
import { Franchise, ModulePermission, Persona, Store, Tenant, User } from '../../../../app/services/api/generated';
import { UpsertUserRequest } from '../../../orderly-for-distributors/ManageUsers/types/UpsertUserRequest';
import HierarchyPicker, { UserAccessLevel } from './components/HierarchyPicker';
import AssignedAccessPanel from './components/AssignedAccessPanel';
import RolePermissionsComponent from './components/RolePermissionsComponent';

// Mirrors Orderly.Inventory.Common.Enums.UserStatusFilter's member names (culture-invariant .ToString() on the backend, not a translated display value).
const USER_STATUS = { Active: 'Active', Invited: 'Invited' } as const;

const EMPTY_REQUEST: UpsertUserRequest = {
  emailAddress: '',
  franchiseName: '',
  personaId: undefined,
  useNewUI: true,
  stores: [],
  franchiseIds: [],
  tenantIds: [],
};

const determineAccessLevel = (user: User | undefined): UserAccessLevel => {
  if (user?.tenants && user.tenants.length > 0) return 'tenant';
  if (user?.franchises && user.franchises.length > 0) return 'franchise';
  return 'store';
};

// Plain standard text - no icon, no coloured/bordered panel.
const InfoNotice = ({ children }: { children: ReactNode }) => (
  <Typography variant="textSM"
    color="text.secondary"
    sx={{ mb: 6, display: 'block' }}>
    {children}
  </Typography>
);

// Overview is read-only outside of Create, so it shows plain label/value pairs rather than disabled inputs.
const LabelValue = ({ label, value }: { label: string; value: ReactNode }) => (
  <Box>
    <Typography variant="textXS"
      sx={{ mb: 1, display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="textSM">
      {value}
    </Typography>
  </Box>
);

const UserDetailPage = () => {
  const { id } = useParams<{ id?: string }>();
  const isCreate = !id;
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('manageUsers');
  const { logError, extractErrorMessage } = useLogError();
  const { user: currentUser, hasPermissionTo } = useContext(UserContext);
  const {
    getPersonas, getModulePermissions, getPermissions, createUser, editUser, resendInvite, fetchUserById,
  } = useNswagClient();

  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [modulePermissions, setModulePermissions] = useState<ModulePermission[]>([]);
  // Keyed by lowercased permission name - only permissions with a description get a " - description" suffix in the UI.
  const [permissionDescriptions, setPermissionDescriptions] = useState<Record<string, string>>({});
  // Everything the detail tiles/Overview show for an existing user - fetched via GET /Users/{id}, not carried over from the grid row.
  const [detailUser, setDetailUser] = useState<User | undefined>(undefined);

  // Scope pickers to what the logged-in user can access - not every tenant/franchise/store in the system.
  const tenants = useMemo<Tenant[]>(() => currentUser?.tenants ?? [], [currentUser]);
  const franchises = useMemo<Franchise[]>(() => currentUser?.franchises ?? [], [currentUser]);
  const stores = useMemo<Store[]>(() => currentUser?.stores ?? [], [currentUser]);

  const [request, setRequest] = useState<UpsertUserRequest>(EMPTY_REQUEST);
  const [originalRequest, setOriginalRequest] = useState<UpsertUserRequest>(EMPTY_REQUEST);
  const [accessLevel, setAccessLevel] = useState<UserAccessLevel>('store');
  const [errors, setErrors] = useState<Partial<Record<keyof UpsertUserRequest, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof UpsertUserRequest, boolean>>>({});

  const [pendingLevel, setPendingLevel] = useState<UserAccessLevel | undefined>(undefined);
  const [showLevelWarning, setShowLevelWarning] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [saveConfirmMessage, setSaveConfirmMessage] = useState<ReactNode>('');
  // Shared between the summary and grid panels so search also highlights matches in the summary.
  const [accessSearch, setAccessSearch] = useState('');
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [inviteSnackbar, setInviteSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [personasResult, modulePermissionsResult, permissionsResult, userResult] = await Promise.all([
          getPersonas(),
          getModulePermissions(),
          getPermissions(),
          isCreate ? Promise.resolve(undefined) : fetchUserById(Number(id)),
        ]);

        if (cancelled) return;

        setPersonas(personasResult?.data ?? []);
        setModulePermissions(modulePermissionsResult ?? []);
        setPermissionDescriptions(Object.fromEntries(
          (permissionsResult?.data?.permissions ?? [])
            .filter(permission => permission.name && permission.description)
            .map(permission => [(permission.name as string).toLowerCase(), permission.description as string]),
        ));

        const fetchedUser = userResult?.data;
        setDetailUser(fetchedUser);

        if (!isCreate && fetchedUser) {
          const level = determineAccessLevel(fetchedUser);
          const tenantIds = fetchedUser.tenants?.filter(item => item.id !== undefined).map(item => item.id ?? -1) ?? [];
          const franchiseIds = fetchedUser.franchises?.filter(item => item.franchiseId !== undefined).map(item => item.franchiseId ?? -1) ?? [];
          const storeNumbers = fetchedUser.stores?.filter(item => item.storeNumber !== undefined).map(item => item.storeNumber ?? '') ?? [];

          const loadedRequest: UpsertUserRequest = {
            emailAddress: fetchedUser.emailAddress ?? '',
            personaId: fetchedUser.persona?.id,
            franchiseName: fetchedUser.franchiseName ?? '',
            useNewUI: true,
            stores: storeNumbers,
            franchiseIds,
            tenantIds,
          };

          setRequest(loadedRequest);
          setOriginalRequest(loadedRequest);
          setAccessLevel(level);
        }
      } catch (error) {
        logError(error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const selectedPersona = useMemo(
    () => personas.find(persona => persona.id === request.personaId),
    [personas, request.personaId],
  );

  const uniqueCompanies = useMemo(
    () => Array.from(new Set(franchises.map(f => f.franchiseName).filter(Boolean))) as string[],
    [franchises],
  );

  const filteredPersonas = useMemo(() => {
    const selectedFranchise = franchises.find(f => f.franchiseName === request.franchiseName);
    const selectedFranchiseId = selectedFranchise?.franchiseId;
    const selectedTenantId = selectedFranchise?.tenantId;

    return personas.filter((persona) => {
      if (persona.tenantIds && persona.tenantIds.length > 0 && selectedTenantId) {
        if (persona.tenantIds.includes(selectedTenantId)) return true;
      }

      if (persona.franchiseIds && persona.franchiseIds.length > 0 && selectedFranchiseId) {
        if (persona.franchiseIds.includes(selectedFranchiseId)) return true;
      }

      if (persona.franchiseName === request.franchiseName) return true;

      if (!persona.franchiseName && persona.tenantId && selectedTenantId === persona.tenantId) return true;

      return false;
    });
  }, [personas, franchises, request.franchiseName]);

  const handleRoleChange = (personaId: number) => {
    setRequest(prev => ({ ...prev, personaId }));
  };

  // Displayed only, never sent back to the API - fetched via GET /Users/{id}, undefined while creating.
  const status = detailUser?.status;
  const lastLoginDate = detailUser?.lastLoginDate;
  const lastInviteSentAt = detailUser?.lastInviteSentAt;
  const fullName = [detailUser?.firstName, detailUser?.lastName].filter(Boolean).join(' ') || '-';

  // Tiles reflect the live in-progress edits, not just the saved user - updates immediately as Role/Access change below.
  const livePermissionsCount = useMemo(
    () => new Set(selectedPersona?.personaRoles ?? []).size,
    [selectedPersona],
  );

  // Admin-in-name permissions granted by the live role - drives the Elevated permissions tile.
  const elevatedPermissionsCount = useMemo(
    () => (selectedPersona?.personaRoles ?? []).filter(role => role.toLowerCase().includes('admin')).length,
    [selectedPersona],
  );

  // Store numbers reachable from the current access assignment, at any level - names the store when there's exactly one.
  const accessStoreNumbers = useMemo(() => {
    if (accessLevel === 'store') return request.stores ?? [];

    const franchiseIds = accessLevel === 'franchise'
      ? (request.franchiseIds ?? [])
      : franchises.filter(f => (request.tenantIds ?? []).includes(f.tenantId ?? -1)).map(f => f.franchiseId ?? -1);

    return stores.filter(store => franchiseIds.includes(store.franchiseId ?? -1)).map(store => store.storeNumber ?? '');
  }, [accessLevel, request.stores, request.franchiseIds, request.tenantIds, franchises, stores]);

  // Franchise ids reachable from the current access assignment, at any level - names the franchise when there's exactly one.
  const accessFranchiseIds = useMemo(() => {
    if (accessLevel === 'franchise') return request.franchiseIds ?? [];
    if (accessLevel === 'tenant') return franchises.filter(f => (request.tenantIds ?? []).includes(f.tenantId ?? -1)).map(f => f.franchiseId ?? -1);

    const storeNumbers = request.stores ?? [];
    return Array.from(new Set(stores.filter(store => storeNumbers.includes(store.storeNumber ?? '')).map(store => store.franchiseId ?? -1)));
  }, [accessLevel, request.franchiseIds, request.tenantIds, request.stores, franchises, stores]);

  // Tenant ids reachable from the current access assignment, at any level - names the tenant when there's exactly one.
  const accessTenantIds = useMemo(() => {
    if (accessLevel === 'tenant') return request.tenantIds ?? [];
    return Array.from(new Set(franchises.filter(f => accessFranchiseIds.includes(f.franchiseId ?? -1)).map(f => f.tenantId ?? -1)));
  }, [accessLevel, request.tenantIds, accessFranchiseIds, franchises]);

  const applyLevelChange = (level: UserAccessLevel) => {
    setAccessLevel(level);
    setRequest(prev => ({ ...prev, tenantIds: [], franchiseIds: [], stores: [] }));
  };

  const toggleInArray = <TItem,>(list: TItem[], value: TItem): TItem[] =>
    (list.includes(value) ? list.filter(existing => existing !== value) : [...list, value]);

  // Level is now chosen upfront via radio buttons - warn only if switching away from a level that already has assignments.
  const handleAccessLevelChange = (newLevel: UserAccessLevel) => {
    if (newLevel === accessLevel) return;

    const hasOtherTypeAssigned = (request.tenantIds?.length ?? 0) > 0
      || (request.franchiseIds?.length ?? 0) > 0
      || (request.stores?.length ?? 0) > 0;

    if (hasOtherTypeAssigned) {
      setPendingLevel(newLevel);
      setShowLevelWarning(true);
    } else {
      applyLevelChange(newLevel);
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof UpsertUserRequest, string>> = {};

    if (!isEmailValid(request.emailAddress)) newErrors.emailAddress = t('invalidEmail');
    if (!request.franchiseName) newErrors.franchiseName = t('requiredfranchiseName');
    if (!request.personaId) newErrors.personaId = t('requiredpersonaId');

    if (accessLevel === 'tenant' && (request.tenantIds?.length ?? 0) === 0) newErrors.tenantIds = t('required', 'Required');
    if (accessLevel === 'franchise' && (request.franchiseIds?.length ?? 0) === 0) newErrors.franchiseIds = t('required', 'Required');
    if (accessLevel === 'store' && (request.stores?.length ?? 0) === 0) newErrors.stores = t('required', 'Required');

    setErrors(newErrors);
    setTouched({ emailAddress: true, franchiseName: true, personaId: true, tenantIds: true, franchiseIds: true, stores: true });
    return Object.keys(newErrors).length === 0;
  };

  const capitalize = (label: string): string => label.charAt(0).toUpperCase() + label.slice(1);

  // Singular/plural label for a tenant/franchise/store count, e.g. "1 store" vs "3 stores".
  const pluralizeUnit = (count: number, unit: UserAccessLevel): string => {
    if (unit === 'tenant') return count === 1 ? t('tenantSingular', 'tenant') : t('tenants', 'tenants');
    if (unit === 'franchise') return count === 1 ? t('franchiseSingular', 'franchise') : t('franchises', 'franchises');
    return count === 1 ? t('storeSingular', 'store') : t('stores');
  };

  const accessSectionLabel = (level: UserAccessLevel): string => {
    const label = level === 'tenant' ? t('tenants', 'tenants') : level === 'franchise' ? t('franchises', 'franchises') : t('stores');
    return capitalize(label);
  };

  // Resolves tenant/franchise ids and store numbers to their display names for the given level.
  const resolveAccessNames = (level: UserAccessLevel, tenantIds: number[], franchiseIds: number[], storeNumbers: string[]): string[] => {
    if (level === 'tenant') return tenantIds.map(tenantId => tenants.find(tenant => tenant.id === tenantId)?.name ?? String(tenantId));
    if (level === 'franchise') return franchiseIds.map(franchiseId => franchises.find(f => f.franchiseId === franchiseId)?.franchiseName ?? String(franchiseId));
    return storeNumbers.map(storeNumber => stores.find(store => store.storeNumber === storeNumber)?.storeName ?? storeNumber);
  };

  // Side-by-side "what was assigned" vs "what will be saved" - names the actual tenants/franchises/stores rather than just a count.
  const buildSaveConfirmContent = (): ReactNode => {
    if (isCreate) return t('confirmCreateUser', 'Create this user with the selected access?');

    const originalLevel: UserAccessLevel = (originalRequest.tenantIds?.length ?? 0) > 0
      ? 'tenant'
      : (originalRequest.franchiseIds?.length ?? 0) > 0
        ? 'franchise'
        : 'store';

    const originalNames = resolveAccessNames(originalLevel, originalRequest.tenantIds ?? [], originalRequest.franchiseIds ?? [], originalRequest.stores ?? []);
    const newNames = resolveAccessNames(accessLevel, request.tenantIds ?? [], request.franchiseIds ?? [], request.stores ?? []);

    const sameArray = (a: (number | string)[], b: (number | string)[]) =>
      JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());

    const unchanged = originalLevel === accessLevel
      && sameArray(originalRequest.tenantIds ?? [], request.tenantIds ?? [])
      && sameArray(originalRequest.franchiseIds ?? [], request.franchiseIds ?? [])
      && sameArray(originalRequest.stores ?? [], request.stores ?? []);

    if (unchanged) return t('confirmSaveUserNoAccessChange', 'Save these changes?');

    return (
      <Stack gap={5}>
        <Typography variant="textSM">{t('confirmSaveUserAccessChangeIntro', 'This will change this user\u2019s access:')}</Typography>
        <Stack direction="row"
          gap={3}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="textXS"
              color="text.secondary"
              sx={{ textTransform: 'uppercase', fontWeight: 600, display: 'block' }}>
              {t('currentAccess', 'Current')}
            </Typography>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ display: 'block', mt: 2 }}>
              {accessSectionLabel(originalLevel)}
            </Typography>
            <Stack>
              {originalNames.length === 0 && <Typography variant="textSM">{'-'}</Typography>}
              {originalNames.map(name => <Typography key={name}
                variant="textSM">{name}</Typography>)}
            </Stack>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="textXS"
              color="text.secondary"
              sx={{ textTransform: 'uppercase', fontWeight: 600, display: 'block' }}>
              {t('newAccess', 'New')}
            </Typography>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ display: 'block', mt: 2 }}>
              {accessSectionLabel(accessLevel)}
            </Typography>
            <Stack>
              {newNames.length === 0 && <Typography variant="textSM">{'-'}</Typography>}
              {newNames.map(name => <Typography key={name}
                variant="textSM">{name}</Typography>)}
            </Stack>
          </Box>
        </Stack>
      </Stack>
    );
  };


  const handleSaveClick = () => {
    if (!validate()) return;
    setSaveConfirmMessage(buildSaveConfirmContent());
    setShowSaveConfirm(true);
  };

  const handleResendInvite = async () => {
    if (!id) return;

    setIsSendingInvite(true);
    try {
      await resendInvite(Number(id));
      // Avoids a full GET /Users/{id} refetch just to reflect the new invite timestamp.
      setDetailUser(prev => (prev ? { ...prev, lastInviteSentAt: new Date().toISOString() } : prev));
      setInviteSnackbar({ open: true, message: t('resendInviteSuccess', 'Invite sent.'), severity: 'success' });
    } catch (error) {
      logError(error);
      setInviteSnackbar({ open: true, message: t('resendInviteError', 'Failed to send invite.'), severity: 'error' });
    } finally {
      setIsSendingInvite(false);
    }
  };

  const handleConfirmSave = async () => {
    setIsSubmitting(true);
    setSubmitErrorMessage(null);
    try {
      if (isCreate) {
        await createUser(request);
      } else {
        await editUser(Number(id), request);
      }
      setShowSaveConfirm(false);
      navigate(`/user-management/users${location.search}`);
    } catch (error) {
      logError(error);
      setShowSaveConfirm(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const status = (error as any)?.response?.status;
      setSubmitErrorMessage(status === 403
        ? t('forbiddenSaveUser', 'You don\'t have sufficient permissions to save this user.')
        : extractErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const resolvedTenants = (request.tenantIds ?? []).map(tenantId => ({
    id: tenantId,
    label: tenants.find(tenant => tenant.id === tenantId)?.name ?? String(tenantId),
  }));
  const resolvedFranchises = (request.franchiseIds ?? []).map(franchiseId => ({
    id: franchiseId,
    label: franchises.find(franchise => franchise.franchiseId === franchiseId)?.franchiseName ?? String(franchiseId),
  }));
  const resolvedStores = (request.stores ?? []).map(storeNumber => ({
    id: storeNumber,
    label: stores.find(store => store.storeNumber === storeNumber)?.storeName ?? storeNumber,
  }));

  // Franchises/stores under the currently assigned tenants/franchises, for the Assigned access drill-down.
  const derivedFranchisesUnderTenants = (request.tenantIds ?? []).length === 0
    ? []
    : franchises
      .filter(f => (request.tenantIds ?? []).includes(f.tenantId ?? -1))
      .map(f => ({ id: f.franchiseId ?? -1, label: f.franchiseName ?? String(f.franchiseId) }));

  const franchisesForPanel = accessLevel === 'tenant' ? derivedFranchisesUnderTenants : resolvedFranchises;

  const derivedStoresUnderFranchises = (() => {
    const franchiseIds = accessLevel === 'tenant'
      ? derivedFranchisesUnderTenants.map(f => f.id)
      : (request.franchiseIds ?? []);

    if (franchiseIds.length === 0) return [];

    return stores
      .filter(store => franchiseIds.includes(store.franchiseId ?? -1))
      .map(store => ({ id: store.storeNumber ?? '', label: store.storeName ?? store.storeNumber ?? '' }));
  })();

  const storesForPanel = accessLevel === 'store' ? resolvedStores : derivedStoresUnderFranchises;

  const tabs: TabDefinition[] = [
    {
      id: 'overview',
      label: t('overview', 'Overview'),
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {!isCreate && (
            <InfoNotice>
              {t('overviewReadOnlyNotice', 'This information can\'t be changed here - the user must update it themselves from their own account settings.')}
            </InfoNotice>
          )}
          <Grid container
            spacing={3}>
            <Grid item
              xs={12}
              md={4}>
              {isCreate ? (
                <TextField
                  fullWidth
                  label={t('email')}
                  value={request.emailAddress}
                  onChange={(event) =>
                    setRequest((prev) => ({
                      ...prev,
                      emailAddress: event.target.value,
                    }))
                  }
                  error={touched.emailAddress && Boolean(errors.emailAddress)}
                  FormHelperTextProps={{
                    style: { display: 'none' },
                  }}
                  size="small"
                />
              ) : (
                <LabelValue label={t('email')}
                  value={request.emailAddress} />
              )}
            </Grid>
            {!isCreate && (
              <>
                <Grid item
                  xs={12}
                  md={4}>
                  <LabelValue label={t('firstName', 'First name')}
                    value={detailUser?.firstName ?? '-'} />
                </Grid>
                <Grid item
                  xs={12}
                  md={4}>
                  <LabelValue label={t('lastName', 'Last name')}
                    value={detailUser?.lastName ?? '-'} />
                </Grid>
                <Grid item
                  xs={12}
                  md={4}>
                  <LabelValue label={t('created', 'Created')}
                    value={detailUser?.createdAt
                      ? `${dayjs(detailUser.createdAt).format('DD/MM/YYYY HH:mm')}${detailUser.createdBy ? ` ${t('by', 'by')} ${detailUser.createdBy}` : ''}`
                      : '-'} />
                </Grid>
                <Grid item
                  xs={12}
                  md={4}>
                  <LabelValue label={t('lastUpdated', 'Last updated')}
                    value={detailUser?.updatedAt
                      ? `${formatUtcToLocalDateTime(detailUser.updatedAt)}${detailUser.updatedBy ? ` ${t('by', 'by')} ${detailUser.updatedBy}` : ''}`
                      : '-'} />
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      ),
    },
    {
      id: 'role-permissions',
      label: t('rolePermissions', 'Role & Permissions'),
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Grid container>
            <Grid item
              xs={12}
              md={4}>
              <FormControl
                fullWidth
                size="small"
                required
                error={touched.franchiseName && Boolean(errors.franchiseName)}
              >
                <InputLabel>{t('company')}</InputLabel>
                <Select
                  label={t('company')}
                  value={request.franchiseName}
                  onChange={(event) =>
                    setRequest((prev) => ({
                      ...prev,
                      franchiseName: event.target.value as string,
                      personaId: undefined,
                    }))
                  }
                >
                  {uniqueCompanies.map((companyName) => (
                    <MenuItem key={companyName}
                      value={companyName}>
                      {companyName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container>
            <Grid item
              xs={12}
              md={4}>
              <FormControl size="small"
                fullWidth
                required
                error={touched.personaId && Boolean(errors.personaId)}>
                <InputLabel>{t('role')}</InputLabel>
                <Select
                  label={t('role')}
                  value={request.personaId ?? ''}
                  onChange={(event) => handleRoleChange(Number(event.target.value))}
                  disabled={!request.franchiseName}
                >
                  {filteredPersonas.map((persona) => (
                    <MenuItem key={persona.id}
                      value={persona.id}>
                      {persona.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <RolePermissionsComponent
            grantedPermissions={selectedPersona?.personaRoles ?? []}
            modulePermissions={modulePermissions}
            permissionDescriptions={permissionDescriptions}
            adminPermissions={currentUser?.roles ?? []}
          />
        </Box>
      ),
    },
    {
      id: 'access',
      label: t('accessLevel'),
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <InfoNotice>
            {t('accessHierarchyIntro', 'Access is granted at one level of the tenant, franchise or store hierarchy. Granting a tenant includes every franchise and store beneath it, granting a franchise includes every store beneath it, and granting a store gives access to only that store.')}
          </InfoNotice>

          <TextField
            size="small"
            value={accessSearch}
            onChange={(event) => setAccessSearch(event.target.value)}
            placeholder={t('search')}
            sx={{ width: 280, maxWidth: '100%' }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              endAdornment: accessSearch && (
                <InputAdornment position="end">
                  <IconButton size="small"
                    aria-label={t('clearFilters')}
                    onClick={() => setAccessSearch('')}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Grid container
            spacing={3}>
            <Grid item
              xs={12}
              md={6}>
              <HierarchyPicker
                tenants={tenants}
                franchises={franchises}
                stores={stores}
                selectedTenantIds={request.tenantIds ?? []}
                selectedFranchiseIds={request.franchiseIds ?? []}
                selectedStoreNumbers={request.stores ?? []}
                onToggleTenant={(tenantId) => setRequest(prev => ({ ...prev, tenantIds: toggleInArray(prev.tenantIds ?? [], tenantId) }))}
                onToggleFranchise={(franchiseId) => setRequest(prev => ({ ...prev, franchiseIds: toggleInArray(prev.franchiseIds ?? [], franchiseId) }))}
                onToggleStore={(storeNumber) => setRequest(prev => ({ ...prev, stores: toggleInArray(prev.stores ?? [], storeNumber) }))}
                level={accessLevel}
                onLevelChange={handleAccessLevelChange}
                search={accessSearch}
                title={accessSectionLabel(accessLevel)}
              />
            </Grid>

            <Grid item
              xs={12}
              md={6}>
              <AssignedAccessPanel
                level={accessLevel}
                search={accessSearch}
                tenants={resolvedTenants}
                franchises={franchisesForPanel}
                stores={storesForPanel}
              />
            </Grid>
            
          </Grid>
        </Box>
      ),
    },
    ...(!isCreate && hasPermissionTo(['ViewUserAudit']) ? [{
      id: 'audit',
      label: t('auditHistory', 'Audit History'),
      content: (
        // Cancels TabbedSection's default content padding so the panel fills the tab edge-to-edge.
        <Box sx={{ m: -9 }}>
          <AuditHistoryPanel
            permission="ViewUserAudit"
            target={ChangeLogTarget.User}
            entityId={String(id)}
          />
        </Box>
      ),
    }] : []),
  ];

  return (
    <>
      <DetailPageTemplate
        title={isCreate ? t('createNewUserTitle', 'Create a new user') : t('editUser', 'Edit User')}
        subtitle={isCreate ? undefined : t('manageExistingUserSubtitle', 'Manage existing user')}
        loading={loading}
        loadingLabel={t('users')}
        actions={(
          <Stack direction="row"
            spacing={2}>
            {(!isCreate && status === USER_STATUS.Invited && hasPermissionTo(['UserManagementResendInvite'])) && (
              <Button
                variant="secondary"
                size="lg"
                onClick={handleResendInvite}
                disabled={isSendingInvite || !hasPermissionTo(['UserManagementResendInvite'])}
              >
                {t('resendInvite', 'Resend invite')}
              </Button>
            )}
            <Button
              variant="secondary"
              size="lg"
              onClick={() => navigate(`/user-management/users${location.search}`)}
            >
              {t('cancel')}
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={handleSaveClick}
              disabled={isSubmitting || !request.franchiseName || !request.personaId || !hasPermissionTo(isCreate ? ['UserManagementCreate'] : ['UserManagementUpdate'])}
            >
              {t('save')}
            </Button>
          </Stack>
        )}
        headlineContent={{
          layout: 'picture-six-wide',
          picture: (
            <PictureTile
              src={detailUser?.imageUrl ?? null}
              alt={request.emailAddress}
              circleSize={{ xs: 160, sm: 180 }}
              fallback={(
                <Avatar sx={{ width: 96, height: 96 }}>
                  <PersonOutlineIcon sx={{ fontSize: 48 }} />
                </Avatar>
              )}
              fallbackLabel={''}
              shape="circle"
            />
          ),
          insights: [
            <InsightTile
              key="identity"
              label={t('identity', 'Identity')}
              value={fullName}
              description={request.emailAddress || '-'}
              icon={<PersonOutlineIcon />}
              variant="primary"
            />,
            <InsightTile
              key="account"
              label={t('accountStatus', 'Account Status')}
              value={status === USER_STATUS.Active ? t('active') : status === USER_STATUS.Invited ? t('invited') : '-'}
              description={status === USER_STATUS.Invited
                ? `${t('lastInviteSent', 'Last invite sent')}: ${lastInviteSentAt ? dayjs(lastInviteSentAt).format('DD/MM/YYYY HH:mm') : '-'}`
                : status === USER_STATUS.Active
                  ? `${t('lastLogin')}: ${lastLoginDate ? dayjs(lastLoginDate).format('DD/MM/YYYY HH:mm') : '-'}`
                  : '-'}
              icon={<PersonOutlineIcon />}
              variant={status === USER_STATUS.Active ? 'success' : status === USER_STATUS.Invited ? 'warning' : 'default'}
            />,
            <InsightTile
              key="userLevel"
              label={t('userLevel', 'User level')}
              value={accessLevel === 'tenant' ? t('tenantLevel') : accessLevel === 'franchise' ? t('franchiseLevel') : t('storeLevel')}
              description={accessTenantIds.length === 1
                ? t('userLevelTenantName', 'Tenant {{name}}', { name: tenants.find(tenant => tenant.id === accessTenantIds[0])?.name ?? accessTenantIds[0] })
                : accessTenantIds.length > 1
                  ? `${accessTenantIds.length} ${pluralizeUnit(accessTenantIds.length, 'tenant')}`
                  : '-'}
              icon={<StoreOutlinedIcon />}
              variant="info"
            />,
            <InsightTile
              key="access"
              label={t('access', 'Access')}
              value={accessStoreNumbers.length === 1
                ? (stores.find(store => store.storeNumber === accessStoreNumbers[0])?.storeName ?? accessStoreNumbers[0])
                : `${accessStoreNumbers.length} ${pluralizeUnit(accessStoreNumbers.length, 'store')}`}
              description={accessFranchiseIds.length === 1
                ? t('accessTileSingleFranchise', 'Across {{name}}', { name: franchises.find(f => f.franchiseId === accessFranchiseIds[0])?.franchiseName ?? accessFranchiseIds[0] })
                : t('accessTileFranchiseCount', 'Across {{count}} {{unit}}', { count: accessFranchiseIds.length, unit: pluralizeUnit(accessFranchiseIds.length, 'franchise') })}
              icon={<StoreOutlinedIcon />}
              variant="success"
            />,
            <InsightTile
              key="role"
              label={t('role')}
              value={selectedPersona?.description ?? '-'}
              description={t('roleGrantsPermissions', 'Grants {{count}} permissions.', { count: livePermissionsCount })}
              icon={<ShieldOutlinedIcon />}
              variant="purple"
            />,
            <InsightTile
              key="elevatedPermissions"
              label={t('elevatedPermissions', 'Elevated permissions')}
              value={String(elevatedPermissionsCount)}
              description={t('elevatedPermissionsDescription', 'Admin permissions')}
              icon={<LockOutlinedIcon />}
              variant="error"
            />,
          ],
        }}
        tabs={tabs}
      />

      <ConfirmationDialog
        open={showLevelWarning}
        variant="primary"
        icon={<SvgIcon><HighlightOffIcon /></SvgIcon>}
        title={t('changeAccessLevelTitle', 'Change access level?')}
        message={t('changeAccessLevelMessage', 'Switching access level will remove this user\u2019s current tenant, franchise and store assignments.')}
        confirmButtonText={t('confirm', 'Confirm')}
        cancelButtonText={t('cancel')}
        onConfirm={() => {
          if (pendingLevel) {
            applyLevelChange(pendingLevel);
          }
          setShowLevelWarning(false);
        }}
        onCancel={() => setShowLevelWarning(false)}
      />

      <ConfirmationDialog
        open={showSaveConfirm}
        variant="primary"
        icon={<SvgIcon><PersonOutlineIcon /></SvgIcon>}
        title={t('confirmSaveTitle', 'Confirm changes')}
        message={t('confirmSaveTitle', 'Confirm changes')}
        messageContent={saveConfirmMessage}
        confirmButtonText={t('save')}
        cancelButtonText={t('cancel')}
        isProcessing={isSubmitting}
        onConfirm={handleConfirmSave}
        onCancel={() => setShowSaveConfirm(false)}
      />

      <MessagesSnackbar
        open={inviteSnackbar.open}
        onClose={() => setInviteSnackbar(prev => ({ ...prev, open: false }))}
        message={inviteSnackbar.message}
        severity={inviteSnackbar.severity}
        duration={4000}
      />

      <MessagesSnackbar
        open={Boolean(submitErrorMessage)}
        onClose={() => setSubmitErrorMessage(null)}
        message={submitErrorMessage ?? ''}
        severity="error"
        duration={6000}
      />
    </>
  );
};

export default UserDetailPage;
